import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "../lib/api";
import { useAccounts } from "../lib/account";
import { moneyFor } from "../lib/currency";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, LineChart, Line, Cell,
} from "recharts";
import {
  ChartLineUp, DownloadSimple, Share, ArrowLeft, TrendUp, TrendDown, Target,
  Trophy, Tag, Copy,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Strategy Report Cards — group trades by tag, show accuracy KPIs, share/PDF.
 */

function StrategyBadge({ label, isUntagged }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest ${
        isUntagged
          ? "bg-secondary text-muted-foreground"
          : "bg-primary/15 text-primary"
      }`}>
      <Tag size={10} weight="fill" />
      {label}
    </span>
  );
}

function Kpi({ label, value, sub, tone, testid }) {
  const toneCls =
    tone === "up" ? "text-emerald-400" : tone === "down" ? "text-red-500" : "text-foreground";
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
      <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{label}</p>
      <p className={`font-mono-num text-2xl font-bold mt-1.5 ${toneCls}`}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

function StrategyRow({ row, onOpen, account }) {
  const positive = row.net_pnl >= 0;
  return (
    <button
      onClick={() => onOpen(row.tag)}
      className="w-full text-left rounded-xl border border-border bg-card p-5 hover:border-primary/40 hover:bg-secondary/40 transition-colors group"
      data-testid={`strategy-row-${row.tag}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2">
            <StrategyBadge label={row.label} isUntagged={row.is_untagged} />
            <span className="text-[10px] text-muted-foreground">{row.total_trades} trades</span>
          </div>
          <p className={`font-mono-num text-3xl font-bold ${positive ? "text-emerald-400" : "text-red-500"}`}>
            {moneyFor(account, row.net_pnl, { sign: true })}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <span>Win rate <b className="text-foreground font-mono-num">{row.win_rate}%</b></span>
            <span>PF <b className="text-foreground font-mono-num">{row.profit_factor}</b></span>
            <span>Expectancy <b className="text-foreground font-mono-num">{moneyFor(account, row.expectancy, { sign: true, short: true })}</b></span>
            <span>Avg R:R <b className="text-foreground font-mono-num">{row.avg_rr}</b></span>
          </div>
        </div>
        <div className={`shrink-0 mt-1 ${positive ? "text-emerald-400" : "text-red-500"}`}>
          {positive ? <TrendUp size={22} weight="fill" /> : <TrendDown size={22} weight="fill" />}
        </div>
      </div>
    </button>
  );
}

function StrategyDetail({ tag, accountId, account, onBack }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!tag || !accountId) return;
    api.get(`/strategies/${encodeURIComponent(tag)}?account_id=${accountId}`)
      .then(({ data }) => setD(data))
      .catch(e => toast.error(e?.response?.data?.detail || "Could not load strategy"));
  }, [tag, accountId]);

  const share = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/strategies/${encodeURIComponent(tag)}/share?account_id=${accountId}`,
      );
      const url = `${window.location.origin}${data.url}`;
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard", { description: url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Share failed");
    } finally { setBusy(false); }
  };

  const exportPdf = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const token = localStorage.getItem("tj_token");
      const url = `${API}/strategies/${encodeURIComponent(tag)}/pdf?account_id=${accountId}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (resp.status === 402) {
        return toast.error("PDF export is a paid feature", { description: "Subscribe to unlock" });
      }
      if (!resp.ok) return toast.error("Export failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `strategy-${tag}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Report downloaded");
    } catch { toast.error("Export failed"); }
    finally { setBusy(false); }
  };

  if (!d) return (
    <div className="rounded-xl border border-border bg-card p-8 text-center text-muted-foreground">
      Loading strategy…
    </div>
  );

  const k = d.kpis;
  const positive = k.net_pnl >= 0;

  return (
    <div className="space-y-6" data-testid={`strategy-detail-${tag}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="strategy-back-btn">
          <ArrowLeft size={14} /> Back to strategies
        </button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={share} disabled={busy} className="rounded-full" data-testid="strategy-share-btn">
            <Share size={14} className="mr-1.5" /> Share link
          </Button>
          <Button onClick={exportPdf} disabled={busy} className="rounded-full" data-testid="strategy-pdf-btn">
            <DownloadSimple size={14} className="mr-1.5" /> Download PDF
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-card to-card p-6">
        <div className="flex items-center gap-2 mb-3">
          <StrategyBadge label={d.label} isUntagged={d.is_untagged} />
          <span className="text-[10px] text-muted-foreground">
            {k.first_trade || "—"} → {k.last_trade || "—"}
          </span>
        </div>
        <p className={`font-mono-num text-5xl font-bold ${positive ? "text-emerald-400" : "text-red-500"}`}>
          {moneyFor(account, k.net_pnl, { sign: true })}
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          Net P&amp;L across {k.total_trades} trades — {k.wins}W / {k.losses}L · {k.win_rate}% win rate
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Win Rate" value={`${k.win_rate}%`} sub={`${k.wins}W / ${k.losses}L`} testid="strategy-kpi-winrate" />
        <Kpi label="Profit Factor" value={k.profit_factor} sub="wins / losses" testid="strategy-kpi-pf" />
        <Kpi label="Expectancy" value={moneyFor(account, k.expectancy, { sign: true, short: true })} sub="per trade" tone={k.expectancy >= 0 ? "up" : "down"} testid="strategy-kpi-expectancy" />
        <Kpi label="Avg R:R" value={k.avg_rr} sub={`Avg win ${moneyFor(account, k.avg_win, { short: true })} · Avg loss ${moneyFor(account, k.avg_loss, { short: true })}`} testid="strategy-kpi-rr" />
        <Kpi label="Long trades" value={k.long_trades} sub={`${k.long_win_rate}% win rate`} testid="strategy-kpi-long" />
        <Kpi label="Short trades" value={k.short_trades} sub={`${k.short_win_rate}% win rate`} testid="strategy-kpi-short" />
        <Kpi label="Best trade" value={k.best_trade ? moneyFor(account, k.best_trade.pnl, { sign: true, short: true }) : "—"} sub={k.best_trade ? `${k.best_trade.symbol} · ${k.best_trade.date}` : ""} tone="up" testid="strategy-kpi-best" />
        <Kpi label="Worst trade" value={k.worst_trade ? moneyFor(account, k.worst_trade.pnl, { sign: true, short: true }) : "—"} sub={k.worst_trade ? `${k.worst_trade.symbol} · ${k.worst_trade.date}` : ""} tone="down" testid="strategy-kpi-worst" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <div className="xl:col-span-3 rounded-xl border border-border bg-card p-6">
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Equity curve</p>
          <div className="h-72 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.equity_curve}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11}
                  tickFormatter={(v) => moneyFor(account, v, { short: true })} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}
                  formatter={(v) => moneyFor(account, v, { sign: true })} />
                <Line type="monotone" dataKey="equity" stroke={positive ? "#10b981" : "#ef4444"} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="xl:col-span-2 rounded-xl border border-border bg-card p-6">
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Top symbols</p>
          <div className="mt-3 space-y-2">
            {k.top_symbols.length === 0 && <p className="text-sm text-muted-foreground">No symbol data yet.</p>}
            {k.top_symbols.map(s => {
              const pos = s.pnl >= 0;
              return (
                <div key={s.symbol} className="flex items-center justify-between p-3 rounded-lg bg-secondary/40 border border-border">
                  <div>
                    <p className="font-medium text-sm">{s.symbol}</p>
                    <p className="text-[11px] text-muted-foreground">{s.trades} trades · {s.win_rate}% win</p>
                  </div>
                  <p className={`font-mono-num font-bold text-sm ${pos ? "text-emerald-400" : "text-red-500"}`}>
                    {moneyFor(account, s.pnl, { sign: true, short: true })}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">All trades in this strategy</p>
        </div>
        <div className="max-h-[420px] overflow-auto hide-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-muted-foreground border-b border-border">
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Symbol</th>
                <th className="px-4 py-2">Side</th>
                <th className="px-4 py-2 text-right">P&amp;L</th>
              </tr>
            </thead>
            <tbody>
              {d.trades.slice().reverse().map(t => (
                <tr key={t.id} className="border-b border-border/60" data-testid={`strategy-trade-${t.id}`}>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{(t.entry_time || "").slice(0, 10)}</td>
                  <td className="px-4 py-2 font-medium">{t.symbol}</td>
                  <td className="px-4 py-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${t.direction === "long" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                      {(t.direction || "").toUpperCase()}
                    </span>
                  </td>
                  <td className={`px-4 py-2 text-right font-mono-num font-bold ${t.pnl >= 0 ? "text-emerald-400" : "text-red-500"}`}>
                    {moneyFor(account, t.pnl, { sign: true })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Strategies() {
  const { activeId, activeAccount } = useAccounts();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    api.get(`/strategies?account_id=${activeId}`)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [activeId]);

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto w-full" data-testid="strategies-page">
      <p className="text-[11px] tracking-[0.3em] uppercase text-muted-foreground">Performance</p>
      <h1 className="font-display text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight mt-2">Strategy Reports</h1>
      <p className="text-base text-muted-foreground mt-2 mb-8 max-w-2xl">
        Each tag on a trade counts as a strategy. Tap a card to see full accuracy KPIs,
        equity curve, and to share or download the report card.
      </p>

      {selected ? (
        <StrategyDetail
          tag={selected}
          accountId={activeId}
          account={activeAccount}
          onBack={() => setSelected(null)} />
      ) : (
        <>
          {data?.overall && (
            <div className="rounded-2xl border border-border bg-card p-6 mb-6" data-testid="strategies-overall">
              <div className="flex items-center gap-2 mb-2">
                <Trophy size={16} className="text-primary" />
                <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Overall (all strategies)</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Net P&amp;L</p>
                  <p className={`font-mono-num text-2xl font-bold ${data.overall.net_pnl >= 0 ? "text-emerald-400" : "text-red-500"}`}>
                    {moneyFor(activeAccount, data.overall.net_pnl, { sign: true })}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Trades</p>
                  <p className="font-mono-num text-2xl font-bold">{data.overall.total_trades}</p>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Win rate</p>
                  <p className="font-mono-num text-2xl font-bold">{data.overall.win_rate}%</p>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Profit factor</p>
                  <p className="font-mono-num text-2xl font-bold">{data.overall.profit_factor}</p>
                </div>
              </div>
            </div>
          )}

          {loading && (
            <div className="text-center text-muted-foreground py-16">Loading strategies…</div>
          )}
          {!loading && data && data.strategies.length === 0 && (
            <div className="rounded-xl border border-dashed border-border p-12 text-center">
              <Target size={32} className="mx-auto text-muted-foreground mb-3" />
              <p className="font-medium">No strategies yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add tags to your trades (e.g. "breakout", "scalp", "swing") to start building strategy reports.
              </p>
            </div>
          )}
          {!loading && data && data.strategies.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="strategies-grid">
              {data.strategies.map(row => (
                <StrategyRow key={row.tag} row={row} onOpen={setSelected} account={activeAccount} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
